/**
 * 팀 관리 헤더의 연결 병동 팝오버(인력 · 스케줄러 미리보기) — 순수 계산만 둔다. 상태와 동작은 app.ts.
 *
 * 팀 관리는 팀만 다룬다(P5). 병동 인력 · 근무표는 **읽기 전용으로 잠깐 보고** 편집은 병동 관리 ·
 * 스케줄러로 건너가서 한다 — 그래서 여기에는 거르기 · 칸 값 풀기만 있고 쓰는 동작이 없다.
 */

/** 채팅 · AI 어시스턴트 패널이 서로 접으라고 주고받는 이벤트(ChatWidget.ts · AiAssistant.ts 와 같은 이름) */
export const FLOATING_PANEL_OPEN_EVENT = "hereby:floating-panel-open";

/** AI 어시스턴트가 열릴 때마다(떠 있는 창 · 도크 모두) 보내는 이벤트 — AiAssistant.ts 와 같은 이름 */
export const AI_ASSISTANT_OPEN_EVENT = "hereby:ai-assistant-open";

/** 이 팝오버가 이벤트에 싣는 이름 — 채팅은 "chat" 이 아니면, 떠 있는 AI 는 "ai" 가 아니면 접는다 */
export const TEAM_WARD_PANEL_ID = "team-ward";

export type TeamWardPopKind = "" | "roster" | "schedule";

/** 헤더 버튼을 눌렀을 때 다음 상태 — 같은 것을 다시 누르면 닫고, 다른 것을 누르면 그것으로 바꾼다(둘이 겹쳐 뜨지 않는다) */
export function nextTeamWardPop(
  current: TeamWardPopKind,
  clicked: TeamWardPopKind,
): TeamWardPopKind {
  return current === clicked ? "" : clicked;
}

/** 시작일부터 종료일까지(양끝 포함) "YYYY-MM-DD" 목록. UTC 로 세어 기기 타임존 · 서머타임에 흔들리지 않는다 */
export function dateRange(startDate: string, endDate: string): string[] {
  const parse = (s: string) => {
    const [y, m, d] = s.split("-").map(Number);
    return Date.UTC(y, m - 1, d);
  };
  const start = parse(startDate);
  const end = parse(endDate);
  if (!(Number.isFinite(start) && Number.isFinite(end)) || end < start) {
    return [];
  }
  const out: string[] = [];
  for (let t = start; t <= end; t += 86_400_000) {
    out.push(new Date(t).toISOString().slice(0, 10));
  }
  return out;
}

/** 근무표 항목 → 칸 값. 스케줄러 loadSchedule 과 같은 규칙: 맞춤 휴가 = 유형 id, 연동 근무 = shift id, 그 밖 = 배정 종류 */
export function entryCellValue(entry: {
  assignmentType: string;
  customLeaveReasonId?: string | null;
  shiftId?: string | null;
}): string {
  if (entry.assignmentType === "leave_custom" && entry.customLeaveReasonId) {
    return entry.customLeaveReasonId;
  }
  if (entry.assignmentType === "shift_custom" && entry.shiftId) {
    return entry.shiftId;
  }
  return entry.assignmentType;
}

/** 인력 팝오버 한 줄 — 서버 병동 요약(team.wardSummary)의 roster 와 같은 모양 */
export interface TeamWardRosterRow {
  employeeId: string;
  name: string;
  jobDuty: string;
  workType: string;
}

/** 인력 팝오버 이름 검색 — 앞뒤 공백을 무시하고 이름에 들어 있으면 남긴다. 빈 검색어면 그대로 */
export function filterRosterByName(rows: TeamWardRosterRow[], query: string): TeamWardRosterRow[] {
  const q = query.trim().toLowerCase();
  return q ? rows.filter((r) => r.name.toLowerCase().includes(q)) : rows;
}
